import { EVENTS, EventName, REGISTRATION_FORM_FOCUS_SESSION_KEY } from "./constants";

// ===== EVENT NAME VALIDATION =====

export function isValidEvent(eventName: string): eventName is EventName {
  return Object.values(EVENTS).includes(eventName as EventName);
}

export function isUserAction(eventName: string): boolean {
  return eventName.startsWith("act:");
}

export function isSystemEvent(eventName: string): boolean {
  return eventName.startsWith("evt:");
}

// ===== DEPOSIT SUCCESS DEDUPLICATION =====
// Shared, capped, TTL-based cache to prevent duplicate deposit-success
// exposure across modules (WS vs polling, etc).

function pruneMap(map: Map<string, number>, ttlMs: number, maxSize: number): void {
  const now = Date.now();
  for (const [key, ts] of map) {
    if (now - ts > ttlMs) {
      map.delete(key);
    }
  }
  const overflow = map.size - maxSize;
  if (overflow > 0) {
    const iterator = map.keys();
    for (let i = 0; i < overflow; i++) {
      const oldestKey = iterator.next().value as string | undefined;
      if (oldestKey === undefined) break;
      map.delete(oldestKey);
    }
  }
}

const MAX_DEPOSIT_TRACKED = 500;
const DEPOSIT_TRACK_TTL_MS = 24 * 60 * 60 * 1000; // 24h
const seenDepositTransactions: Map<string, number> = new Map();

export function hasSeenAndMarkDeposit(txId: string): boolean {
  pruneMap(seenDepositTransactions, DEPOSIT_TRACK_TTL_MS, MAX_DEPOSIT_TRACKED);
  if (seenDepositTransactions.has(txId)) return true;
  seenDepositTransactions.set(txId, Date.now());
  return false;
}

// ===== DEPOSIT FEEDBACK-BUNDLE DEDUPLICATION =====
// Guards the WHOLE feedback bundle (toast + balance + analytics + ack) so that
// WS / polling / reconciliation cannot double-fire feedback for the same order
// within a session. Distinct from hasSeenAndMarkDeposit (which guards only the
// analytics event); using a separate map avoids one path's mark suppressing the
// other's legitimate call.
const MAX_DEPOSIT_FEEDBACK_TRACKED = 500;
const DEPOSIT_FEEDBACK_TTL_MS = 24 * 60 * 60 * 1000; // 24h
const seenDepositFeedback: Map<string, number> = new Map();

export function hasSeenAndMarkDepositFeedback(orderRef?: string | null): boolean {
  if (!orderRef) return false;
  pruneMap(seenDepositFeedback, DEPOSIT_FEEDBACK_TTL_MS, MAX_DEPOSIT_FEEDBACK_TRACKED);
  if (seenDepositFeedback.has(orderRef)) return true;
  seenDepositFeedback.set(orderRef, Date.now());
  return false;
}

// ===== CLOSE DEPOSIT IFRAME DEDUPLICATION =====

const MAX_CLOSE_DEPOSIT_TRACKED = 1000;
const CLOSE_DEPOSIT_TTL_MS = 24 * 60 * 60 * 1000;
const seenCloseDepositKeys: Map<string, number> = new Map();

/**
 * Dedup by combination of orderRef + reason.
 * Returns true if the combo has been seen within TTL (event should be skipped).
 */
export function hasSeenAndMarkCloseDeposit(orderRef?: string | null, reason?: string | null): boolean {
  if (!orderRef || !reason) return false;
  pruneMap(seenCloseDepositKeys, CLOSE_DEPOSIT_TTL_MS, MAX_CLOSE_DEPOSIT_TRACKED);
  const key = `${orderRef}:${reason}`;
  if (seenCloseDepositKeys.has(key)) return true;
  seenCloseDepositKeys.set(key, Date.now());
  return false;
}

// ===== SESSION-FLAG UTILITIES =====

// Thunk, not a Storage: reading localStorage/sessionStorage itself throws when storage is blocked, so read inside the try.
function hasSeenAndMark(getStorage: () => Storage, key: string): boolean {
  try {
    const storage = getStorage();
    if (storage.getItem(key)) return true;
    storage.setItem(key, "1");
    return false;
  } catch {
    return false;
  }
}

export function hasSeenAndMarkOncePerSession(key: string): boolean {
  return hasSeenAndMark(() => sessionStorage, key);
}

export function clearOncePerSession(key: string): void {
  try {
    sessionStorage.removeItem(key);
  } catch {}
}

export function hasSeenAndMarkRegistrationFormFocus(method?: string): boolean {
  const key = method ? `${REGISTRATION_FORM_FOCUS_SESSION_KEY}:${method}` : REGISTRATION_FORM_FOCUS_SESSION_KEY;
  return hasSeenAndMarkOncePerSession(key);
}

export function resetRegistrationFormFocusOncePerSession(method?: string): void {
  if (method) {
    clearOncePerSession(`${REGISTRATION_FORM_FOCUS_SESSION_KEY}:${method}`);
  } else {
    clearOncePerSession(`${REGISTRATION_FORM_FOCUS_SESSION_KEY}:google_login`);
    clearOncePerSession(`${REGISTRATION_FORM_FOCUS_SESSION_KEY}:username_password`);
    clearOncePerSession(`${REGISTRATION_FORM_FOCUS_SESSION_KEY}:phone_pin`);
    clearOncePerSession(REGISTRATION_FORM_FOCUS_SESSION_KEY);
  }
}

// ===== PWA INSTALL SUCCESS (persistent, once per device/install) =====

const PWA_INSTALL_SUCCESS_KEY = "analytics:pwa_install_success_fired";

// Per-origin, never cleared on logout: once per install/device, not per account.
export function hasSeenAndMarkPwaInstallSuccess(): boolean {
  return hasSeenAndMark(() => localStorage, PWA_INSTALL_SUCCESS_KEY);
}
